<template>
  <div
    class="rs-form-rating-star"
    :style="cssVars"
  >
    <div
      v-if="label || helpTooltip || $slots.tailLabel"
      class="rs-form-rating-star__label-row"
    >
      <label
        v-if="label"
        :for="id"
      >{{ label }}<sup v-if="showRequiredMark">*</sup></label>
      <span v-if="helpTooltip">
        <RsIcon
          :id="tooltipId"
          icon="question-circle"
          :weight="helpTooltipWeight"
          class="rs-form-rating-star__help"
        />
        <RsTooltip
          :target="tooltipId"
          :title="helpTooltip"
          :position="helpTooltipPosition"
          :delay="{ show: 200 }"
        />
      </span>
      <slot name="tailLabel" />
    </div>
    <Field
      v-slot="{ errors }"
      v-model="input"
      class="veeValidate"
      as="div"
      :name="validationName"
      :rules="rules"
    >
      <div class="rs-form-rating-star__control">
        <span
          v-if="lowLabel"
          class="rs-form-rating-star__bound"
        >{{ lowLabel }}</span>
        <div
          :id="id"
          class="rs-form-rating-star__stars"
          :class="{ 'rs-form-rating-star__stars--disabled': disabled }"
        >
          <i
            v-for="star in max"
            :key="star"
            class="rs-form-rating-star__star"
            :class="star <= (input || 0) ? 'fas fa-star' : 'far fa-star'"
            @click="setRating(star)"
          />
        </div>
        <span
          v-if="highLabel"
          class="rs-form-rating-star__bound"
        >{{ highLabel }}</span>
        <span
          v-if="clearable && input"
          class="rs-form-rating-star__clear"
          @click="clear"
        >{{ clearText }}</span>
      </div>
      <div
        v-if="errors.length"
        class="text--error"
      >
        {{ errors[0] }}
      </div>
    </Field>
  </div>
</template>
<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { Field, defineRule } from 'vee-validate';
import { required } from '@vee-validate/rules';
import type { TTooltipPosition } from '@/shared/types';
import i18n from '@/i18n';

defineRule('required', (value: unknown) => (required(value) ? true : i18n.t('validation.fieldRequired') as string));

type TRatingValue = number | null;
type TRatingRules = string | Record<string, unknown>;

export default defineComponent({
  name: 'RsFormRatingStar',
  components: {
    Field,
  },
  props: {
    value: {
      type: Number as PropType<TRatingValue>,
      default: null,
    },
    /**
     * Vue 3 v-model binding. Kept alongside the legacy `value`/`input` pair so
     * both `v-model` (modelValue/update:modelValue) and explicit `:value`+`@input`
     * consumers work. `undefined` means the parent did not bind v-model, so we
     * fall back to `value` (see the `boundValue` computed).
     */
    modelValue: {
      type: Number as PropType<TRatingValue>,
      default: undefined,
    },
    id: {
      type: String,
      default: '',
    },
    label: String,
    name: {
      type: String,
      default: 'rating',
    },
    required: {
      type: Boolean,
      default: false,
    },
    rules: {
      type: [String, Object] as PropType<TRatingRules>,
      default: undefined,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Number of stars to render.
     */
    max: {
      type: Number,
      default: 5,
    },
    /**
     * Filled-star colour. Applied via a CSS custom property so the disabled
     * state can still override it from the stylesheet.
     */
    color: {
      type: String,
      default: '#694CD6',
    },
    /**
     * When true, renders a "clear" control that resets the rating to null.
     */
    clearable: {
      type: Boolean,
      default: false,
    },
    clearText: {
      type: String,
      default: 'clear',
    },
    /**
     * Labels flanking the stars. Pass an empty string to hide either one.
     */
    lowLabel: {
      type: String,
      default: 'Low',
    },
    highLabel: {
      type: String,
      default: 'High',
    },
    helpTooltip: {
      type: String,
      default: '',
    },
    helpTooltipPosition: {
      type: String as PropType<TTooltipPosition>,
      default: 'right',
    },
    /**
     * FontAwesome weight for the help tooltip icon: far | fas | fal | fad
     */
    helpTooltipWeight: {
      type: String,
      default: 'fal',
    },
    useNameForValidation: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['input', 'update:modelValue', 'change'],
  data() {
    return {
      input: (this.modelValue !== undefined ? this.modelValue : this.value) as TRatingValue,
    };
  },
  computed: {
    /**
     * The externally-bound value, preferring the Vue 3 `modelValue` (v-model)
     * when present and falling back to the legacy `value` prop otherwise.
     */
    boundValue(): TRatingValue {
      return this.modelValue !== undefined ? this.modelValue : this.value;
    },
    cssVars(): Record<string, string> {
      return { '--rs-form-rating-star-color': this.color };
    },
    tooltipId(): string {
      return `form-rating-star-helper-${this.id || this.name}`;
    },
    validationName(): string {
      if (this.useNameForValidation && this.name) {
        return this.name;
      }
      return this.label || this.name || 'field';
    },
    showRequiredMark(): boolean {
      if (this.required) return true;
      if (typeof this.rules === 'string') return this.rules.split('|').includes('required');
      return !!(this.rules && this.rules.required);
    },
  },
  watch: {
    boundValue: {
      immediate: true,
      handler(): void {
        this.input = this.boundValue;
      },
    },
  },
  methods: {
    setRating(star: number): void {
      if (this.disabled) return;
      this.input = star;
      this.emitValue(star);
    },
    clear(): void {
      if (this.disabled) return;
      this.input = null;
      this.emitValue(null);
    },
    emitValue(value: TRatingValue): void {
      this.$emit('input', value);
      this.$emit('update:modelValue', value);
      this.$emit('change', value);
    },
  },
});
</script>
<style lang="scss">
@import '@/styles/_variables.scss';

.rs-form-rating-star {
  &__label-row {
    margin-bottom: 0.5rem;

    label {
      display: inline-block;
      margin-bottom: 0;
      font-size: $form-label-font-size;
      font-weight: $form-label-font-weight;
    }
  }

  &__help {
    color: $secondary-color;
    margin-left: 0.25rem;
  }

  &__control {
    display: inline-flex;
    align-items: center;
  }

  &__bound {
    color: $tertiary-muted;
    font-size: 14px;
  }

  &__stars {
    display: inline-flex;
    align-items: center;
    // Fixed height (matching the former BFormRating form-control-lg) so the
    // stars stay vertically centred alongside the Low/High labels.
    height: 40px;
    padding: 0 4px;
  }

  &__star {
    // 15px glyph + 3.75px side margins reproduce BFormRating's star size
    // and 7.5px inter-star spacing.
    font-size: 0.9375rem;
    color: var(--rs-form-rating-star-color, #694CD6);
    cursor: pointer;
    transition: transform 0.15s ease-in-out;
    margin: 0 3.75px;
  }

  // Hover preview: enlarge the hovered star, mirroring BFormRating's scale-up.
  &__stars:not(&__stars--disabled) &__star:hover {
    transform: scale(1.5);
  }

  &__stars--disabled {
    background-color: #e9ecef;
    border-radius: 0.3rem;

    .rs-form-rating-star__star {
      cursor: default;
      color: #6c757d;
    }
  }

  &__clear {
    margin-left: 0.5rem;
    color: $secondary-color;
    cursor: pointer;
  }
}
</style>
